import {
	customerPortalUserProjectTable,
	linearCycleTable,
	linearIssueLabelTable,
	linearIssueTable,
	linearLabelTable,
	linearWorkflowStateTable,
} from "@mojo/db/schema";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import {
	and,
	count,
	desc,
	eq,
	gte,
	inArray,
	isNotNull,
	isNull,
	notInArray,
	sql,
} from "drizzle-orm";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";

export type DashboardStats = {
	totalIssues: number;
	resolvedIssues: number;
	resolutionRate: number;
	cycleSuccessRate: number | null;
	openBugs: number;
	inProgress: number;
	plannedFeatures: number;
};

export type ActivityItem = {
	id: string;
	type: "created" | "resolved" | "updated";
	title: string;
	identifier: string;
	time: Date;
};

export type CycleInfo = {
	name: string | null;
	number: number;
	startsAt: string;
	endsAt: string;
	progress: number;
	totalIssues: number;
	completedIssues: number;
	isCurrent: boolean;
};

export type CyclesOverview = {
	current: CycleInfo | null;
	previous: CycleInfo[];
};

export type IssuesTrendPoint = {
	week: string;
	created: number;
	resolved: number;
};

// Helper to get user's project IDs
async function getUserProjectIds(userId: string): Promise<string[]> {
	const userProjects = await db
		.select({ projectId: customerPortalUserProjectTable.projectId })
		.from(customerPortalUserProjectTable)
		.where(eq(customerPortalUserProjectTable.userId, userId));
	return userProjects.map((p) => p.projectId);
}

// Get dashboard statistics
export const getDashboardStats = createServerFn({ method: "GET" }).handler(
	async (): Promise<DashboardStats> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return {
				totalIssues: 0,
				resolvedIssues: 0,
				resolutionRate: 0,
				cycleSuccessRate: null,
				openBugs: 0,
				inProgress: 0,
				plannedFeatures: 0,
			};
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return {
				totalIssues: 0,
				resolvedIssues: 0,
				resolutionRate: 0,
				cycleSuccessRate: null,
				openBugs: 0,
				inProgress: 0,
				plannedFeatures: 0,
			};
		}

		// Get workflow states by type and name
		const workflowStates = await db
			.select({
				id: linearWorkflowStateTable.id,
				name: linearWorkflowStateTable.name,
				type: linearWorkflowStateTable.type,
			})
			.from(linearWorkflowStateTable);

		const completedStateIds = workflowStates
			.filter((s) => s.type === "completed")
			.map((s) => s.id);
		// In Progress: status name "In Progress" or "In Review"
		const inProgressStateIds = workflowStates
			.filter((s) => s.name === "In Progress" || s.name === "In Review")
			.map((s) => s.id);

		// Get bug and feature label IDs (exact match)
		const labels = await db
			.select({ id: linearLabelTable.id, name: linearLabelTable.name })
			.from(linearLabelTable);

		// Bugs: exact label "Bug"
		const bugLabelIds = labels.filter((l) => l.name === "Bug").map((l) => l.id);
		// Features: exact label "Feature"
		const featureLabelIds = labels
			.filter((l) => l.name === "Feature")
			.map((l) => l.id);

		// Base condition for user's projects
		const baseCondition = and(
			inArray(linearIssueTable.projectId, projectIds),
			isNull(linearIssueTable.archivedAt),
		);

		// Total issues
		const [{ total: totalIssues }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(baseCondition);

		// Resolved issues (completed state)
		const [{ total: resolvedIssues }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(
				and(
					baseCondition,
					completedStateIds.length > 0
						? inArray(linearIssueTable.stateId, completedStateIds)
						: sql`false`,
				),
			);

		// In progress issues
		const [{ total: inProgress }] = await db
			.select({ total: count() })
			.from(linearIssueTable)
			.where(
				and(
					baseCondition,
					inProgressStateIds.length > 0
						? inArray(linearIssueTable.stateId, inProgressStateIds)
						: sql`false`,
				),
			);

		// Open bugs (issues with "Bug" label, not completed)
		let openBugs = 0;
		if (bugLabelIds.length > 0 && completedStateIds.length > 0) {
			const bugIssues = await db
				.select({ issueId: linearIssueLabelTable.issueId })
				.from(linearIssueLabelTable)
				.where(inArray(linearIssueLabelTable.labelId, bugLabelIds));
			const bugIssueIds = bugIssues.map((i) => i.issueId);

			if (bugIssueIds.length > 0) {
				const [{ total }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							baseCondition,
							inArray(linearIssueTable.id, bugIssueIds),
							notInArray(linearIssueTable.stateId, completedStateIds),
						),
					);
				openBugs = total;
			}
		}

		// Planned features (issues with "Feature" label, in backlog, excluding sub-issues)
		const backlogStateIds = workflowStates
			.filter((s) => s.type === "backlog")
			.map((s) => s.id);

		let plannedFeatures = 0;
		if (featureLabelIds.length > 0 && backlogStateIds.length > 0) {
			const featureIssues = await db
				.select({ issueId: linearIssueLabelTable.issueId })
				.from(linearIssueLabelTable)
				.where(inArray(linearIssueLabelTable.labelId, featureLabelIds));
			const featureIssueIds = featureIssues.map((i) => i.issueId);

			if (featureIssueIds.length > 0) {
				const [{ total }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.where(
						and(
							baseCondition,
							inArray(linearIssueTable.id, featureIssueIds),
							isNull(linearIssueTable.parentId), // Exclude sub-issues
							inArray(linearIssueTable.stateId, backlogStateIds), // Only backlog
						),
					);
				plannedFeatures = total;
			}
		}

		// Cycle success rate (% of issues in completed cycles that are done)
		let cycleSuccessRate: number | null = null;
		if (completedStateIds.length > 0) {
			const today = new Date().toISOString().split("T")[0];

			// Count issues in completed cycles (cycles that have ended)
			const [{ total: totalInCycles }] = await db
				.select({ total: count() })
				.from(linearIssueTable)
				.innerJoin(
					linearCycleTable,
					eq(linearIssueTable.cycleId, linearCycleTable.id),
				)
				.where(
					and(
						baseCondition,
						isNotNull(linearIssueTable.cycleId),
						sql`${linearCycleTable.endsAt} < ${today}`,
					),
				);

			if (totalInCycles > 0) {
				// Count completed issues in completed cycles
				const [{ total: completedInCycles }] = await db
					.select({ total: count() })
					.from(linearIssueTable)
					.innerJoin(
						linearCycleTable,
						eq(linearIssueTable.cycleId, linearCycleTable.id),
					)
					.where(
						and(
							baseCondition,
							isNotNull(linearIssueTable.cycleId),
							sql`${linearCycleTable.endsAt} < ${today}`,
							inArray(linearIssueTable.stateId, completedStateIds),
						),
					);

				cycleSuccessRate = Math.round(
					(completedInCycles / totalInCycles) * 100,
				);
			}
		}

		const resolutionRate =
			totalIssues > 0 ? Math.round((resolvedIssues / totalIssues) * 100) : 0;

		return {
			totalIssues,
			resolvedIssues,
			resolutionRate,
			cycleSuccessRate,
			openBugs,
			inProgress,
			plannedFeatures,
		};
	},
);

// Get recent activity
export const getRecentActivity = createServerFn({ method: "GET" }).handler(
	async (): Promise<ActivityItem[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		// Get workflow states by type
		const workflowStates = await db
			.select({
				id: linearWorkflowStateTable.id,
				type: linearWorkflowStateTable.type,
			})
			.from(linearWorkflowStateTable);

		const completedStateIds = workflowStates
			.filter((s) => s.type === "completed")
			.map((s) => s.id);

		// Get recently updated issues
		const recentIssues = await db
			.select({
				id: linearIssueTable.id,
				identifier: linearIssueTable.identifier,
				title: linearIssueTable.title,
				stateId: linearIssueTable.stateId,
				createdAt: linearIssueTable.createdAt,
				completedAt: linearIssueTable.completedAt,
				linearUpdatedAt: linearIssueTable.linearUpdatedAt,
			})
			.from(linearIssueTable)
			.where(
				and(
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
				),
			)
			.orderBy(desc(linearIssueTable.linearUpdatedAt))
			.limit(10);

		// Map to activity items
		const activities: ActivityItem[] = recentIssues.map((issue) => {
			let type: "created" | "resolved" | "updated" = "updated";

			// Check if recently created (within 1 minute of update time)
			const createdRecently =
				issue.linearUpdatedAt &&
				Math.abs(issue.linearUpdatedAt.getTime() - issue.createdAt.getTime()) <
					60000;

			if (createdRecently) {
				type = "created";
			} else if (issue.stateId && completedStateIds.includes(issue.stateId)) {
				type = "resolved";
			}

			return {
				id: issue.id,
				type,
				title: issue.title,
				identifier: issue.identifier,
				time: issue.linearUpdatedAt || issue.createdAt,
			};
		});

		return activities;
	},
);

// Get cycles overview (current + 2 previous)
export const getCyclesOverview = createServerFn({ method: "GET" }).handler(
	async (): Promise<CyclesOverview> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return { current: null, previous: [] };
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return { current: null, previous: [] };
		}

		const today = new Date().toISOString().split("T")[0];

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Helper to get cycle stats
		async function getCycleStats(
			cycleId: string,
		): Promise<{ totalIssues: number; completedIssues: number }> {
			const [{ total: totalIssues }] = await db
				.select({ total: count() })
				.from(linearIssueTable)
				.where(
					and(
						eq(linearIssueTable.cycleId, cycleId),
						inArray(linearIssueTable.projectId, projectIds),
						isNull(linearIssueTable.archivedAt),
					),
				);

			const [{ total: completedIssues }] = await db
				.select({ total: count() })
				.from(linearIssueTable)
				.where(
					and(
						eq(linearIssueTable.cycleId, cycleId),
						inArray(linearIssueTable.projectId, projectIds),
						isNull(linearIssueTable.archivedAt),
						completedStateIds.length > 0
							? inArray(linearIssueTable.stateId, completedStateIds)
							: sql`false`,
					),
				);

			return { totalIssues, completedIssues };
		}

		// Get current or next cycle
		const [currentCycle] = await db
			.select()
			.from(linearCycleTable)
			.where(
				and(
					gte(linearCycleTable.endsAt, today),
					isNull(linearCycleTable.archivedAt),
				),
			)
			.orderBy(linearCycleTable.startsAt)
			.limit(1);

		// Get last 2 completed cycles
		const previousCycles = await db
			.select()
			.from(linearCycleTable)
			.where(
				and(
					sql`${linearCycleTable.endsAt} < ${today}`,
					isNull(linearCycleTable.archivedAt),
				),
			)
			.orderBy(desc(linearCycleTable.endsAt))
			.limit(2);

		let current: CycleInfo | null = null;
		if (currentCycle) {
			const stats = await getCycleStats(currentCycle.id);
			current = {
				name: currentCycle.name,
				number: currentCycle.number,
				startsAt: currentCycle.startsAt,
				endsAt: currentCycle.endsAt,
				progress: currentCycle.progress,
				totalIssues: stats.totalIssues,
				completedIssues: stats.completedIssues,
				isCurrent: true,
			};
		}

		const previous: CycleInfo[] = await Promise.all(
			previousCycles.map(async (cycle) => {
				const stats = await getCycleStats(cycle.id);
				return {
					name: cycle.name,
					number: cycle.number,
					startsAt: cycle.startsAt,
					endsAt: cycle.endsAt,
					progress: cycle.progress,
					totalIssues: stats.totalIssues,
					completedIssues: stats.completedIssues,
					isCurrent: false,
				};
			}),
		);

		return { current, previous };
	},
);

// Get issues trend data (created vs resolved per week)
export const getIssuesTimeSeries = createServerFn({ method: "GET" }).handler(
	async (): Promise<IssuesTrendPoint[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		// Get completed workflow states
		const completedStates = await db
			.select({ id: linearWorkflowStateTable.id })
			.from(linearWorkflowStateTable)
			.where(eq(linearWorkflowStateTable.type, "completed"));
		const completedStateIds = completedStates.map((s) => s.id);

		// Get all issues for user's projects (non-archived)
		const issues = await db
			.select({
				id: linearIssueTable.id,
				linearCreatedAt: linearIssueTable.linearCreatedAt,
				completedAt: linearIssueTable.completedAt,
				stateId: linearIssueTable.stateId,
			})
			.from(linearIssueTable)
			.where(
				and(
					inArray(linearIssueTable.projectId, projectIds),
					isNull(linearIssueTable.archivedAt),
				),
			);

		if (issues.length === 0) {
			return [];
		}

		// Find earliest issue date to limit chart range
		const earliestDate = issues.reduce((earliest, issue) => {
			return issue.linearCreatedAt < earliest
				? issue.linearCreatedAt
				: earliest;
		}, issues[0].linearCreatedAt);

		// Generate months from earliest issue date to now (max 12 months)
		const months: { start: Date; end: Date; label: string }[] = [];
		const now = new Date();
		const maxMonthsBack = 11;

		// Calculate how many months back the earliest issue is
		const earliestMonth = new Date(
			earliestDate.getFullYear(),
			earliestDate.getMonth(),
			1,
		);
		const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
		const monthsDiff = Math.floor(
			(currentMonth.getTime() - earliestMonth.getTime()) /
				(1000 * 60 * 60 * 24 * 30),
		);
		const monthsToShow = Math.min(monthsDiff + 1, maxMonthsBack + 1);

		for (let i = monthsToShow - 1; i >= 0; i--) {
			const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
			const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
			monthEnd.setHours(23, 59, 59, 999);

			const shortYear = monthStart.getFullYear().toString().slice(-2);
			months.push({
				start: monthStart,
				end: monthEnd,
				label: `${monthStart.toLocaleDateString("en-US", { month: "short" })} ${shortYear}`,
			});
		}

		// Aggregate by month
		const result: IssuesTrendPoint[] = months.map(({ start, end, label }) => {
			const created = issues.filter((issue) => {
				const createdAt = issue.linearCreatedAt;
				return createdAt >= start && createdAt <= end;
			}).length;

			const resolved = issues.filter((issue) => {
				if (!issue.completedAt) return false;
				if (!issue.stateId || !completedStateIds.includes(issue.stateId))
					return false;
				return issue.completedAt >= start && issue.completedAt <= end;
			}).length;

			return { week: label, created, resolved };
		});

		return result;
	},
);
